// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { ArrowUpRight, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  PLUGIN_MARKETPLACE_NAME,
  type AgentKind,
  type OrgSettings,
  type PluginMarketplaceStatus,
} from '@greenlight/shared';

import {
  buildClaudeManagedInstall,
  buildClaudeBypassSettings,
  buildClaudeManagedSettings,
  CLAUDE_CODE_SYNC_MIN_VERSION,
  formatClaudeSettingsJson,
} from '@/lib/claudeManagedSettings';
import {
  buildCodexGuardianRequirement,
  buildCodexApprovalDefaults,
  buildCodexMemberConfig,
  CODEX_NO_PROMPTS_LINES,
  buildCodexRunEverythingDefaults,
  buildCodexMcpRequirements,
} from '@/lib/codexPermissionPolicy';
import {
  buildCursorCliPermissions,
  buildCursorPermissions,
  formatPolicyJson,
} from '@/lib/cursorPermissions';

import { cn } from '@/lib/utils';

import { AdminLink } from './AdminLink';
import { AgentMark, CLAUDE_MARK, CODEX_MARK, CURSOR_MARK } from './AgentMark';
import { CodeBlock } from './CodeBlock';
import { CommandSteps } from './CommandSteps';
import { CopilotManagedSettingsCards } from './CopilotManagedSettingsCards';
import { CopyRow } from './CopyRow';
import { OrgInstructionsCard } from './OrgInstructionsCard';
import { MARKETPLACE_CREATE_PATH } from './pluginSetup';

interface AgentInstructionsProps {
  agent: AgentKind;
  repository: PluginMarketplaceStatus;
  agentSetup: OrgSettings['agent_setup'];
  /**
   * Whether to render the cards that only an IT admin can act on — Claude
   * organization plugin sync, managed-settings and organization-instructions rollouts, the
   * Cursor team-marketplace import, auto-run allowlist and team rule, the Codex organization
   * install and policy, and Copilot enterprise managed-settings. Fails closed.
   */
  showOrgRollout: boolean;
}

function Tag({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </span>
  );
}

function CodexInstructions({
  repository,
  sparsePath,
  mcpUrl,
  showOrgRollout,
}: {
  repository: PluginMarketplaceStatus;
  sparsePath: string | null;
  mcpUrl: string;
  showOrgRollout: boolean;
}): React.ReactElement {
  const approvalDefaults = buildCodexApprovalDefaults();
  const runEverythingDefaults = buildCodexRunEverythingDefaults();
  const memberConfig = buildCodexMemberConfig();
  const [codexPlan, setCodexPlan] = React.useState<'agent-security' | 'none'>('agent-security');
  const guardianRequirement = buildCodexGuardianRequirement();
  const managedRequirements = buildCodexMcpRequirements(mcpUrl);

  return (
    <div className="flex flex-col gap-4">
      {showOrgRollout && (
        <>
          <SectionCard
            title="Codex — organization install"
            icon={<AgentMark {...CODEX_MARK} />}
            headerBar
            badge={<Tag>IT rollout</Tag>}
          >
            <div className="flex flex-col gap-3 p-5 text-sm text-muted-foreground">
              <Steps>
                <li>
                  Give ChatGPT&rsquo;s GitHub plugin access to this repository: open your
                  organization&rsquo;s{' '}
                  <AdminLink href={githubAppsUrl(repository.repo_full_name)}>
                    Installed GitHub Apps
                  </AdminLink>
                  , select <Strong>Configure</Strong> beside{' '}
                  <Strong>ChatGPT Codex Connector</Strong>, and add this repository under{' '}
                  <Strong>Repository access</Strong>.
                </li>
                <li>
                  Open <AdminLink href="https://chatgpt.com/admin/plugins">Plugins</AdminLink> in
                  the admin console, select <Strong>Add → Import marketplace</Strong>, paste the
                  repository URL below, and connect GitHub if asked.
                </li>
                <li>
                  On the Plugins page, open the tab named after your workspace, open{' '}
                  <Strong>Greenlight</Strong>, and set <Strong>Workspace default</Strong> to{' '}
                  <Strong>Installed</Strong>.
                </li>
              </Steps>
              <Details hint="Sync, Desktop app only, conflicts" testId="codex-install-details">
                <li>
                  OpenAI serves a synced copy, so members need no GitHub access; only your GitHub
                  connection reads the repository.
                </li>
                <li>
                  OpenAI syncs daily; <Strong>Marketplaces</Strong> in the admin console&rsquo;s
                  sidebar has <Strong>Sync now</Strong>.
                </li>
                <li>
                  OpenAI lists Greenlight as <Strong>Desktop app only</Strong> because it includes
                  an MCP server, so members use it in Codex in the ChatGPT desktop app.
                </li>
                <li>
                  Two marketplaces that both export a plugin named greenlight conflict: delete the
                  old marketplace, which removes its plugins, before importing the new one.
                </li>
              </Details>
            </div>
            <div className="divide-y divide-border">
              <CopyRow
                label="Repository URL"
                value={`https://github.com/${repository.repo_full_name}`}
              />
            </div>
          </SectionCard>
          <SectionCard
            title="Codex — let Greenlight run without approval prompts"
            icon={<AgentMark {...CODEX_MARK} />}
            headerBar
            badge={<Tag>IT rollout</Tag>}
          >
            <div className="flex flex-col gap-3 p-5 text-sm text-muted-foreground">
              <PlanToggle
                label="Does your admin console have an Agent security page?"
                value={codexPlan}
                onChange={setCodexPlan}
                testId="codex-plan-toggle"
                options={[
                  { value: 'agent-security', label: 'Yes, Agent security' },
                  { value: 'none', label: 'No Agent security page' },
                ]}
              />
              {codexPlan === 'agent-security' ? (
                <>
                  <Steps>
                    <li>
                      Open the <AdminLink href="https://chatgpt.com/admin">admin console</AdminLink>
                      , select <Strong>Agent security</Strong> in the sidebar, then{' '}
                      <Strong>Workspace default</Strong>.
                    </li>
                    <li>
                      In <Strong>Defaults → Command execution</Strong>, set{' '}
                      <Strong>Approval reviewer</Strong> to automatic review.
                    </li>
                    <li>
                      Select <Strong>Edit as TOML</Strong>. In the Global <Strong>Defaults</Strong>{' '}
                      document (<span className="font-mono">config.toml</span>), add the first block
                      below.
                    </li>
                    <li>
                      In the Global <Strong>Requirements</Strong> document (
                      <span className="font-mono">requirements.toml</span>), add the second block
                      above the first line in square brackets. If{' '}
                      <span className="font-mono">guardian_policy_config</span> is already there,
                      add the sentence inside its text instead, since a second one would replace it.
                    </li>
                    <li>
                      Select <Strong>Save changes</Strong>.
                    </li>
                  </Steps>
                  <CodeBlock caption="DEFAULTS · CONFIG.TOML" code={approvalDefaults} />
                  <CodeBlock
                    caption="REQUIREMENTS · REQUIREMENTS.TOML"
                    code={guardianRequirement}
                  />
                </>
              ) : (
                <>
                  <Steps>
                    <li>
                      Each member adds this block to{' '}
                      <span className="font-mono text-foreground">~/.codex/config.toml</span>.
                    </li>
                    <li>Restart Codex.</li>
                  </Steps>
                  <CodeBlock caption="~/.CODEX/CONFIG.TOML" code={memberConfig} />
                </>
              )}
            </div>
            <div className="flex flex-col gap-3 px-5 pb-5">
              <Disclosure
                label="Optional: no approval prompts at all"
                hint="Never ask with Full access"
                testId="codex-no-prompts"
              >
                <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
                  {codexPlan === 'agent-security' ? (
                    <>
                      <p>
                        If your organization would rather Codex never asked, use this Defaults block
                        instead of the first one above. Codex calls it <Strong>Never ask</Strong>{' '}
                        with <Strong>Full access</Strong>; under{' '}
                        <Strong>Requirements → Policy guardrails</Strong>, leave both allowed (a
                        guardrail left unset allows every choice), and set them under{' '}
                        <Strong>Defaults → Command execution</Strong> too.
                      </p>
                      <CodeBlock caption="DEFAULTS · CONFIG.TOML" code={runEverythingDefaults} />
                    </>
                  ) : (
                    <>
                      <p>
                        If a member would rather Codex never asked, they add these two lines at the
                        top of the same file. Codex calls it <Strong>Never ask</Strong> with{' '}
                        <Strong>Full access</Strong>.
                      </p>
                      <CodeBlock
                        caption="~/.CODEX/CONFIG.TOML · ADD AT THE TOP"
                        code={CODEX_NO_PROMPTS_LINES}
                      />
                    </>
                  )}
                </div>
              </Disclosure>
              <Disclosure
                label="If your requirements list MCP servers"
                hint="Codex turns off plugin servers that aren't listed"
                testId="codex-mcp-allowlist"
              >
                <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
                  <p>
                    Once your Requirements list any plugin&rsquo;s MCP servers, Codex turns off
                    every plugin server that has no entry. Add this one to the Global Requirements
                    document.
                  </p>
                  <CodeBlock
                    caption="REQUIREMENTS · REQUIREMENTS.TOML"
                    code={managedRequirements}
                  />
                </div>
              </Disclosure>
              <Details hint="What these settings change" testId="codex-approval-details">
                <li>
                  Codex asks before Greenlight&rsquo;s tools run and before its commands leave the
                  sandbox. These settings keep Codex&rsquo;s own review and let its automatic
                  reviewer approve Greenlight by your policy instead of asking the member.
                </li>
                <li>Codex reads its configuration at startup.</li>
              </Details>
            </div>
          </SectionCard>
        </>
      )}
      {!showOrgRollout && (
        <p className="text-sm text-muted-foreground" data-testid="codex-check-first">
          IT usually installs Greenlight for the whole workspace. Check the Codex desktop
          app&rsquo;s plugin list — if Greenlight is there, you&rsquo;re set.
        </p>
      )}
      <SelfServe agentLabel="Codex">
        <p className="text-sm text-muted-foreground">
          Open <Strong>Settings → Marketplaces → Add more marketplaces</Strong> and paste these
          values. Codex clones the repository from GitHub, so a private one needs your Git access.
        </p>
        <div className="divide-y divide-border rounded-gl border border-border">
          <CopyRow label="Repository" value={repository.repo_full_name} />
          <CopyRow label="Ref" value={repository.default_branch} />
          <CopyRow
            label="Sparse path · optional"
            value={sparsePath ?? '—'}
            copyable={sparsePath !== null}
          />
        </div>
      </SelfServe>
    </div>
  );
}

/** Two-way switch for steps that differ by the vendor plan or console an organization has. */
function PlanToggle<T extends string>({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  testId: string;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === value}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-11 rounded-gl border px-3 text-sm md:h-9',
              option.value === value
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-background text-foreground hover:bg-muted/50',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Steps({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground marker:text-foreground">
      {children}
    </ol>
  );
}

/** The GitHub organization's installed-apps page for the repository's owner. */
function githubAppsUrl(repoFullName: string): string {
  return `https://github.com/organizations/${repoFullName.split('/')[0]}/settings/installations`;
}

/** Collapsed background a card keeps reachable without putting it ahead of the steps. */
function Details({
  hint,
  testId,
  children,
}: {
  hint: string;
  testId?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Disclosure label="Details" hint={hint} testId={testId}>
      <ul className="flex list-disc flex-col gap-2 px-4 pb-4 pl-9 pt-1 text-sm text-muted-foreground">
        {children}
      </ul>
    </Disclosure>
  );
}

function Strong({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="font-medium text-foreground">{children}</span>;
}

/** Self-serve install steps: the rare path, so they stay collapsed until asked for. */
function SelfServe({
  agentLabel,
  children,
}: {
  agentLabel: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Disclosure
      label="Install it yourself"
      hint={`Only if IT hasn't rolled ${agentLabel} out`}
      testId="agent-self-serve"
    >
      <div className="flex flex-col gap-3 px-4 pb-4 pt-1">{children}</div>
    </Disclosure>
  );
}

function ClaudeInstructions({
  repository,
  privateRepository,
  mcpUrl,
  showOrgRollout,
  privateInstall,
}: {
  repository: PluginMarketplaceStatus;
  privateRepository: string | null;
  mcpUrl: string;
  showOrgRollout: boolean;
  privateInstall: boolean;
}): React.ReactElement {
  const syncRepository =
    repository.visibility === 'private' ? repository.repo_full_name : privateRepository;
  const perUser = [
    `/plugin marketplace add ${repository.repo_full_name}`,
    `/plugin install greenlight@${PLUGIN_MARKETPLACE_NAME}`,
  ];
  const bypassSettings = formatClaudeSettingsJson(buildClaudeBypassSettings());
  const approvalPolicy = formatClaudeSettingsJson(buildClaudeManagedSettings());
  const marketplaceEntry = formatClaudeSettingsJson(
    buildClaudeManagedInstall(repository.repo_full_name),
  );
  return (
    <div className="flex flex-col gap-4">
      {showOrgRollout && (
        <>
          {!privateInstall && (
            <SectionCard
              title="1 · Install Greenlight for everyone"
              icon={<AgentMark {...CLAUDE_MARK} />}
              headerBar
              badge={<Tag>CLAUDE ORG</Tag>}
            >
              <div className="flex flex-col gap-3 p-5 text-sm text-muted-foreground">
                {syncRepository !== null && syncRepository !== repository.repo_full_name && (
                  <p data-testid="claude-org-uses-private">
                    Claude syncs only private repositories, so these steps use{' '}
                    <Strong>{syncRepository}</Strong>.
                  </p>
                )}
                <Steps>
                  {syncRepository === null && (
                    <li data-testid="claude-org-needs-private">
                      Create a private agent plugin repo under{' '}
                      <Link
                        to={MARKETPLACE_CREATE_PATH}
                        className="font-medium text-foreground underline underline-offset-4"
                      >
                        Settings → Source control
                      </Link>
                      , or ask an organization admin to. Claude syncs only private repositories.
                    </li>
                  )}
                  <li>
                    Open{' '}
                    <AdminLink href="https://claude.ai/admin-settings/skills">
                      Organization settings → Plugins &amp; skills
                    </AdminLink>
                    , select <Strong>Add → Sync from GitHub</Strong>, choose{' '}
                    {syncRepository === null ? (
                      'that repository'
                    ) : (
                      <Strong>{syncRepository}</Strong>
                    )}{' '}
                    and leave <Strong>Sync automatically</Strong> on.
                  </li>
                  <li>
                    If the repository is missing from the list, select{' '}
                    <Strong>Install the Claude GitHub App</Strong> in the dialog, or give the{' '}
                    <Strong>Claude</Strong> app access under your organization&rsquo;s{' '}
                    <AdminLink href={githubAppsUrl(syncRepository ?? repository.repo_full_name)}>
                      Installed GitHub Apps
                    </AdminLink>
                    .
                  </li>
                  <li>
                    Set the Greenlight plugin&rsquo;s <Strong>Default access</Strong> to{' '}
                    <Strong>Installed by default</Strong> or <Strong>Required</Strong>.
                  </li>
                  <li>
                    Open{' '}
                    <AdminLink href="https://claude.ai/admin-settings/connectors">
                      Organization settings → Connectors
                    </AdminLink>{' '}
                    and add the Greenlight connector. Each person signs in to Greenlight on first
                    use.
                  </li>
                </Steps>
                <Details hint="Where it reaches, sync" testId="claude-org-details">
                  <li>
                    Reaches claude.ai, Claude Desktop (chat, Cowork and the Code tab) and the Claude
                    Code CLI signed in with claude.ai, version {CLAUDE_CODE_SYNC_MIN_VERSION} or
                    later. Anthropic serves a synced copy, so members need no GitHub access.
                  </li>
                  <li>
                    People who signed in on an older Claude Code version run{' '}
                    <span className="font-mono">/login</span> again before the plugin syncs.
                  </li>
                  <li>
                    Keep <span className="font-mono">syncClaudeAiPlugins</span> on and Skills
                    enabled for claude.ai: turning either off stops plugin sync.
                  </li>
                  <li>
                    A copy someone installed themselves outranks the organization plugin. They
                    remove it with{' '}
                    <span className="font-mono">/plugin uninstall greenlight@greenlight-tools</span>
                    .
                  </li>
                </Details>
              </div>
              {syncRepository !== null && (
                <div className="divide-y divide-border">
                  <CopyRow label="Repository" value={syncRepository} />
                </div>
              )}
            </SectionCard>
          )}
          <SectionCard
            title={`${privateInstall ? '1' : '2'} · Let Greenlight run without approval prompts`}
            icon={<AgentMark {...CLAUDE_MARK} />}
            headerBar
            badge={<Tag>CLAUDE ORG</Tag>}
          >
            <div className="flex flex-col gap-3 p-5">
              <Steps>
                <li>
                  Open{' '}
                  <AdminLink href="https://claude.ai/admin-settings/claude-code">
                    Organization settings → Claude Code
                  </AdminLink>{' '}
                  and select <Strong>Manage</Strong> beside <Strong>Managed settings</Strong>.
                </li>
                <li>
                  Merge this policy into the managed settings and save. If they set{' '}
                  <span className="font-mono">disableAutoMode</span>, remove it.
                </li>
              </Steps>
              <CodeBlock caption="CLAUDE MANAGED SETTINGS" code={approvalPolicy} />
              <Disclosure
                label="Optional: no permission prompts at all"
                hint="bypassPermissions"
                testId="claude-bypass-option"
              >
                <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
                  <p>
                    If your organization would rather Claude Code never asked, merge this instead.
                    It starts every session with permission prompts off, so every tool call runs at
                    once. Leave <span className="font-mono">disableBypassPermissionsMode</span>{' '}
                    unset.
                  </p>
                  <CodeBlock caption="CLAUDE MANAGED SETTINGS · NO PROMPTS" code={bypassSettings} />
                </div>
              </Disclosure>
              {!privateInstall && (
                <Alert variant="warning" data-testid="claude-no-plugin-entry">
                  <TriangleAlert aria-hidden="true" />
                  <AlertDescription>
                    <p>
                      Don&rsquo;t add <span className="font-mono">enabledPlugins</span> or{' '}
                      <span className="font-mono">extraKnownMarketplaces</span> for Greenlight here:
                      Claude would then skip the organization plugin from step 1, and with a private
                      repository nobody gets Greenlight.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              <Details hint="What the policy does, MCP allowlist, sandboxing">
                <li>
                  The policy keeps Auto Mode, where Claude&rsquo;s own checks review each call, and
                  approves Greenlight&rsquo;s tools,{' '}
                  <span className="font-mono">greenlight run</span> and the development command it
                  starts. Auto Mode is on by default for Team and Enterprise.
                </li>
                <li>
                  If your managed settings set <span className="font-mono">allowedMcpServers</span>,
                  add your Greenlight MCP URL to it: that list applies to plugin servers too.
                </li>
                <li>
                  If Bash sandboxing is on, also allow your Greenlight hostname and the CLI install
                  and credential paths; neither mode overrides the sandbox.
                </li>
              </Details>
            </div>
          </SectionCard>
          <OrgInstructionsCard
            agent="claude"
            mcpUrl={mcpUrl}
            title={`${privateInstall ? '2' : '3'} · Tell Claude when to use Greenlight`}
          />
          <Disclosure
            label="Claude Code without a claude.ai sign-in"
            hint="API key, Bedrock or Vertex"
            defaultOpen={privateInstall}
            testId="claude-unsynced-install"
          >
            <div className="flex flex-col gap-3 px-4 pb-4 pt-1">
              <Steps>
                <li>
                  Give those machines <Strong>Git access</Strong> to this agent plugin repo.
                </li>
                <li>
                  Push this entry to those machines only, in a{' '}
                  <span className="font-mono">managed-settings.json</span> file through your device
                  management, alongside the approval policy above. Not in the Claude admin console:
                  it would hide the organization plugin from everyone who signs in.
                </li>
              </Steps>
              <CodeBlock caption="MARKETPLACE ENTRY" code={marketplaceEntry} />
            </div>
          </Disclosure>
        </>
      )}
      {!showOrgRollout && (
        <p className="text-sm text-muted-foreground" data-testid="claude-check-first">
          IT usually rolls Greenlight out to everyone. Type{' '}
          <span className="font-mono text-foreground">/greenlight</span> in Claude Code — if it
          resolves, you&rsquo;re set.
        </p>
      )}
      <SelfServe agentLabel="Claude">
        <p className="text-sm text-muted-foreground">
          In the <Strong>Claude Code CLI</Strong>, run these once in any project. They clone the
          repository from GitHub, so a private one needs your Git access.
        </p>
        <CommandSteps caption="CLAUDE CODE CLI" commands={perUser} />
        <p className="text-sm text-muted-foreground" data-testid="claude-self-serve-upkeep">
          Turn on auto-update for greenlight-tools under <Strong>/plugin → Marketplaces</Strong>; it
          is off by default. This copy outranks your organization&rsquo;s plugin, so uninstall it
          once IT rolls Greenlight out.
        </p>
        {privateInstall ? (
          <p className="text-sm text-muted-foreground" data-testid="private-install-clients">
            This install is only reachable on the company network. These clients work: Claude Code
            CLI and the Desktop Code tab, Cursor desktop, and Codex desktop.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            <Strong>claude.ai and Claude Desktop</Strong> have no self-serve plugin install: the
            plugin arrives from your organization. Until then you can add the MCP URL below as a
            connector under <Strong>Settings → Connectors</Strong>, on plans that let you add your
            own connectors.
          </p>
        )}
        <div className="divide-y divide-border rounded-gl border border-border">
          <CopyRow label="Org MCP URL" value={mcpUrl} />
        </div>
      </SelfServe>
    </div>
  );
}

function CopilotInstructions({
  repository,
  mcpUrl,
  showOrgRollout,
}: {
  repository: PluginMarketplaceStatus;
  mcpUrl: string;
  showOrgRollout: boolean;
}): React.ReactElement {
  const cli = [
    `copilot plugin marketplace add ${repository.repo_full_name}`,
    `copilot plugin install greenlight@${PLUGIN_MARKETPLACE_NAME}`,
  ];
  return (
    <div className="flex flex-col gap-4">
      {showOrgRollout && (
        <>
          <CopilotManagedSettingsCards
            marketplaceRepo={repository.repo_full_name}
            mcpUrl={mcpUrl}
          />
        </>
      )}
      <SelfServe agentLabel="Copilot">
        <p className="text-sm text-muted-foreground">
          In the <Strong>Copilot CLI</Strong>, run these once. They fetch the repository with your
          GitHub sign-in, so a private one needs your read access to it.
        </p>
        <CommandSteps caption="COPILOT CLI" commands={cli} />
        <p className="text-sm text-muted-foreground" data-testid="copilot-self-serve-allow-all">
          Copilot asks before the <span className="font-mono">greenlight</span> command runs; allow
          it once for the session when asked. If you want no prompts at all, start with{' '}
          <span className="font-mono">copilot --allow-all</span> or run{' '}
          <span className="font-mono">/allow-all</span> in a session.
        </p>
      </SelfServe>
      <Details hint="VS Code, desktop app, cloud agent" testId="copilot-surfaces">
        <li>
          VS Code and the Copilot desktop app install the same Greenlight plugin from this
          marketplace, through the plugin settings GitHub documents for each.
        </li>
        <li>
          Copilot&rsquo;s cloud agent and code review can&rsquo;t use Greenlight: they run with
          nobody signed in, and Greenlight records every call under the person who made it.
        </li>
      </Details>
    </div>
  );
}

function CursorInstructions({
  repository,
  mcpUrl,
  showOrgRollout,
}: {
  repository: PluginMarketplaceStatus;
  mcpUrl: string;
  showOrgRollout: boolean;
}) {
  const permissions = formatPolicyJson(buildCursorPermissions());
  const cliPermissions = formatPolicyJson(buildCursorCliPermissions());
  const [cursorPlan, setCursorPlan] = React.useState<'enterprise' | 'teams'>('enterprise');
  return (
    <div className="flex flex-col gap-4">
      {showOrgRollout && (
        <SectionCard
          title="Cursor — team marketplace"
          icon={<AgentMark {...CURSOR_MARK} />}
          headerBar
          badge={<Tag>IT rollout</Tag>}
        >
          <div className="flex flex-col gap-3 p-5 text-sm text-muted-foreground">
            <Steps>
              <li>
                Give the <Strong>Cursor</Strong> app access to this repository: open your
                organization&rsquo;s{' '}
                <AdminLink href={githubAppsUrl(repository.repo_full_name)}>
                  Installed GitHub Apps
                </AdminLink>
                , select <Strong>Configure</Strong> beside Cursor, and add it under{' '}
                <Strong>Repository access</Strong>.
              </li>
              <li>
                Open{' '}
                <AdminLink href="https://cursor.com/dashboard/plugins">
                  Cursor Dashboard → Plugins &amp; MCPs
                </AdminLink>{' '}
                and add it with <Strong>Add Marketplace → Import from Repo</Strong>.
              </li>
              <li>
                In Marketplace Settings, turn on <Strong>Serve Marketplace From Cursor</Strong> and{' '}
                <Strong>Enable Auto Refresh</Strong>.
              </li>
              <li>
                Set greenlight to <Strong>Default On</Strong> or <Strong>Required</Strong>.
              </li>
            </Steps>
            <Details hint="Serving, updates, one marketplace" testId="cursor-install-details">
              <li>
                Serve Marketplace From Cursor keeps a synced copy, so members need no GitHub access.
                Auto Refresh picks up plugin updates.
              </li>
              <li>
                Teams allows one team marketplace; replacing it uninstalls the old one&rsquo;s
                plugins for the team.
              </li>
            </Details>
          </div>
          <div className="divide-y divide-border">
            <CopyRow
              label="Repository URL"
              value={`https://github.com/${repository.repo_full_name}`}
            />
          </div>
        </SectionCard>
      )}
      {showOrgRollout && (
        <SectionCard
          title="Cursor — let Greenlight run without approval prompts"
          icon={<AgentMark {...CURSOR_MARK} />}
          headerBar
          badge={<Tag>IT rollout</Tag>}
        >
          <div className="flex flex-col gap-3 p-5">
            <PlanToggle
              label="Which Cursor plan does your organization have?"
              value={cursorPlan}
              onChange={setCursorPlan}
              testId="cursor-plan-toggle"
              options={[
                { value: 'enterprise', label: 'Enterprise' },
                { value: 'teams', label: 'Teams' },
              ]}
            />
            <Steps>
              <li>
                Keep Run Mode on <Strong>Auto-review</Strong>, Cursor&rsquo;s default.
              </li>
              {cursorPlan === 'enterprise' ? (
                <li>
                  Open{' '}
                  <AdminLink href="https://cursor.com/dashboard">
                    Cursor Dashboard → Team Settings
                  </AdminLink>{' '}
                  → <Strong>Auto Run Configuration</Strong> and add the entries in the first block.
                </li>
              ) : (
                <li>
                  Each member adds the entries in the first block under{' '}
                  <Strong>Cursor Settings → Agents → Approvals &amp; Execution</Strong>, or picks{' '}
                  <Strong>Always Run</Strong> on the first Greenlight call.
                </li>
              )}
              <li>
                For the Cursor CLI, merge the second block into its{' '}
                <span className="font-mono text-foreground">cli-config.json</span> permissions.
              </li>
            </Steps>
            <CodeBlock caption="PERMISSIONS.JSON" code={permissions} />
            <CodeBlock caption="CLI-CONFIG.JSON" code={cliPermissions} />
            <Disclosure
              label="Optional: no approval prompts at all"
              hint="Run Everything, chosen by each member"
              testId="cursor-run-everything"
            >
              <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
                <p>
                  If your organization would rather Cursor never asked, members set Run Mode to{' '}
                  <Strong>Run Everything</Strong> in the same settings, where every tool call runs
                  at once.
                  {cursorPlan === 'enterprise'
                    ? ' Admins choose which Run Modes members may use under Team Settings; keep it allowed.'
                    : ''}
                </p>
              </div>
            </Disclosure>
            <Disclosure
              label="Machines you already manage"
              hint="Pushing the file locks Run Mode to Allowlist"
              testId="cursor-managed-file"
            >
              <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
                <p>
                  The file above can be pushed to{' '}
                  <span className="font-mono text-foreground">~/.cursor/permissions.json</span> on
                  macOS or Windows. While its allowlists are present, Cursor locks Run Mode to
                  Allowlist and turns off Auto-review and Run Everything, so set the entries in the
                  dashboard or the app instead wherever either of those modes is wanted. The file
                  replaces the in-app allowlist, so merge any entries members added, including their
                  Always Run approvals.
                </p>
              </div>
            </Disclosure>
            <Details hint="What runs, team list, MCP list" testId="cursor-approval-details">
              <li>
                Auto-review has Cursor&rsquo;s own classifier review each call; allowlisted calls
                run at once. On Enterprise, team settings apply to every member and override their
                own; Teams has no central setting for this.
              </li>
              <li>
                The shell allow covers the greenlight command, including greenlight run and its
                child. Trusting this rule also trusts that child command.
              </li>
              <li>
                If <span className="font-medium text-foreground">Team list enforcement</span> is on
                under the dashboard&rsquo;s Plugins page, add the greenlight plugin to the Team
                list, or Cursor blocks its MCP server.
              </li>
              <li>
                <span className="font-medium text-foreground">Enterprise only:</span> add the MCP
                URL below under Team Settings → MCP Configuration only when that list is already on.
                Preserve existing servers and leave Tools empty. An active list blocks servers that
                are not on it.
              </li>
              <li>
                The shell allow does not cover node &lt;plugin-cache&gt;/cli/greenlight.mjs. Add a
                rule for the exact trusted script path if needed; a broad node argument glob also
                permits unrelated code. Sandbox network rules are separate: allow this
                install&rsquo;s Greenlight hostname for terminal commands.
              </li>
            </Details>
          </div>
          <div className="divide-y divide-border">
            <CopyRow label="MCP URL" value={mcpUrl} />
          </div>
        </SectionCard>
      )}
      {showOrgRollout && <OrgInstructionsCard agent="cursor" mcpUrl={mcpUrl} />}
      {!showOrgRollout && (
        <p className="text-sm text-muted-foreground" data-testid="cursor-check-first">
          IT usually rolls Greenlight out through the team marketplace. Open{' '}
          <Strong>Customize</Strong> in Cursor&rsquo;s sidebar — if greenlight is listed, install it
          there (or it&rsquo;s already installed).
        </p>
      )}
      <SelfServe agentLabel="Cursor">
        <p className="text-sm text-muted-foreground">
          Run this in the Agent chat, then install greenlight from <Strong>Customize</Strong>. It
          clones the repository from GitHub, so a private one needs your Git access, and Cursor
          keeps a personal import on the version it first fetched.
        </p>
        <p className="text-sm text-muted-foreground" data-testid="cursor-self-serve-allowlist">
          Keep Run Mode on <Strong>Auto-review</Strong> and add the Greenlight MCP servers and the{' '}
          <span className="font-mono">greenlight</span> command to its allowlist in{' '}
          <Strong>Cursor Settings → Agents → Approvals &amp; Execution</Strong>, or choose{' '}
          <Strong>Always Run</Strong> on the first Greenlight approval. If you want no prompts at
          all, choose <Strong>Run Everything</Strong> there instead.
        </p>
        <CommandSteps
          caption="CURSOR AGENT CHAT"
          commands={[`/add-plugin https://github.com/${repository.repo_full_name}`]}
        />
      </SelfServe>
    </div>
  );
}

function OtherInstructions({
  repository,
  mcpUrl,
}: {
  repository: PluginMarketplaceStatus;
  mcpUrl: string;
}) {
  const skillHref = `https://github.com/${repository.repo_full_name}/blob/${repository.default_branch}/plugins/greenlight/skills/greenlight/SKILL.md`;
  return (
    <SectionCard title="Other agents" headerBar badge={<Tag>fallback</Tag>}>
      <div className="px-5 py-3.5 text-sm text-muted-foreground">
        Use the org MCP URL and universal Skill with agents that do not support the packaged plugin.
      </div>
      <div className="divide-y divide-border">
        <CopyRow label="Org MCP URL" value={mcpUrl} />
        <div className="flex items-center justify-between gap-3 px-5 py-3.5">
          <span className="text-sm text-foreground">Universal Skill</span>
          <a
            href={skillHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open SKILL.md (opens in new tab)"
            className="inline-flex items-center gap-1 font-mono text-[13px] text-link hover:underline"
          >
            SKILL.md
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </SectionCard>
  );
}

export function AgentInstructions({
  agent,
  repository,
  agentSetup,
  showOrgRollout,
}: AgentInstructionsProps): React.ReactElement {
  switch (agent) {
    case 'claude':
      return (
        <ClaudeInstructions
          repository={repository}
          privateRepository={
            agentSetup.marketplace_repositories.find((row) => row.visibility === 'private')
              ?.repo_full_name ?? null
          }
          mcpUrl={agentSetup.mcp_url}
          showOrgRollout={showOrgRollout}
          privateInstall={agentSetup.private_install === true}
        />
      );
    case 'cursor':
      return (
        <CursorInstructions
          repository={repository}
          mcpUrl={agentSetup.mcp_url}
          showOrgRollout={showOrgRollout}
        />
      );
    case 'other':
      return <OtherInstructions repository={repository} mcpUrl={agentSetup.mcp_url} />;
    case 'codex':
      return (
        <CodexInstructions
          repository={repository}
          sparsePath={agentSetup.sparse_path}
          mcpUrl={agentSetup.mcp_url}
          showOrgRollout={showOrgRollout}
        />
      );
    case 'copilot':
      return (
        <CopilotInstructions
          repository={repository}
          mcpUrl={agentSetup.mcp_url}
          showOrgRollout={showOrgRollout}
        />
      );
    default:
      return agent satisfies never;
  }
}
